/**
 * Context provider for managing and filtering dashboard events.
 * It fetches events metrics based on network and school year dates
 * and provides a mechanism to filter them by site IDs.
 */
import React, { createContext, useContext, useMemo, useState } from 'react';
import useCoreEventsMetrics from "@/hooks/useCoreEventsMetrics";
import useCoreEventsMetricsInvitation from "@/hooks/useCoreEventsMetricsInvitations";

const EventsContext = createContext();

export function EventsProvider({ children, network, schoolStart, schoolEnd }) {
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);

  const { data: eventData, isLoading: isLoadingEvents } = useCoreEventsMetrics({
    networkId: network?.id,
    startDate: schoolStart,
    endDate: schoolEnd,
  });

  const { data: invitationData, isLoading: isLoadingInvitations } = useCoreEventsMetricsInvitation({
    networkId: network?.id,
    startDate: schoolStart,
    endDate: schoolEnd,
  })

  const events = eventData || [];
  const invitations = invitationData || [];

  const selectedSiteIdSet = useMemo(() => {
    return new Set(selectedSiteIds);
  }, [selectedSiteIds]);

  const filteredEvents = useMemo(() => {
    if (selectedSiteIdSet.size === 0) return events;
    return events.filter(event => selectedSiteIdSet.has(event.siteId));
  }, [events, selectedSiteIdSet]);

  const filteredInvitations = useMemo(() => {
    if (selectedSiteIdSet.size === 0) return invitations;
    return invitations.filter(invitation => selectedSiteIdSet.has(invitation.siteId));
  }, [invitations, selectedSiteIdSet]);

  /**
   * @typedef {Object} EventMetricContext
   * @property {EventMetricItem[]} events
   * @property {EventMetricItem[]} filteredEvents
   * @property {EventMetricInvitationItem[]} invitations
   * @property {EventMetricInvitationItem[]} filteredInvitations
   * @property {number[]} selectedSiteIds
   * @property {Function} setSelectedSiteIds
   * @property {boolean} isLoading
   * @property {boolean} isLoadingEvents
   * @property {boolean} isLoadingInvitations
   * @property {object} network
   * @property {string} schoolStart
   * @property {string} schoolEnd
   */
  const value = {
    events,
    filteredEvents,
    invitations,
    filteredInvitations,
    selectedSiteIds,
    setSelectedSiteIds,
    isLoadingEvents,
    isLoadingInvitations,
    isLoading: isLoadingEvents || isLoadingInvitations,
    network,
    schoolStart,
    schoolEnd
  };

  return (
    <EventsContext.Provider value={value}>
      {children}
    </EventsContext.Provider>
  );
}

/**
 * @returns {EventMetricContext}
 */
export function useEventsContext() {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error('useEventsContext must be used within an EventsProvider');
  }
  return context;
}
